import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, Save, Check, X, Plus, ListChecks } from "lucide-react";
import { toast } from "sonner";

/**
 * One row in the Admin's PCSP Goal Matrix. Owns its own title /
 * description / support_items draft state and talks to
 * /api/clients/{cid}/goals/{gid} directly. The parent matrix re-renders
 * on save/delete via the `onSaved` / `onDeleted` callbacks.
 */
export function GoalEditor({ clientId, goal, onSaved, onDeleted }) {
  const [title, setTitle] = useState(goal.title || "");
  const [description, setDescription] = useState(goal.description || "");
  const [items, setItems] = useState(() => Array.isArray(goal.support_items) ? goal.support_items.slice() : []);
  const [newItem, setNewItem] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    const v = newItem.trim();
    if (!v) return;
    if (items.includes(v)) { toast.info("Already on the list"); return; }
    setItems((arr) => [...arr, v]);
    setNewItem("");
  };

  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${clientId}/goals/${goal.id}`, {
        title, description, support_items: items,
      });
      onSaved?.(data);
      setEditing(false);
      toast.success("Goal saved — contractor's Daily Note matrix updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${goal.title}"? This hides it from contractor Daily Notes.`)) return;
    try {
      await api.delete(`/clients/${clientId}/goals/${goal.id}`);
      onDeleted?.();
      toast.success("Goal removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="border-2 border-border rounded-md p-3 space-y-2 bg-white" data-testid={`admin-goal-${goal.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {editing ? (
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="font-semibold" data-testid={`goal-edit-title-${goal.id}`} />
          ) : (
            <p className="font-display font-semibold text-sm">{goal.title}</p>
          )}
          {editing ? (
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Support details (strategies, prompts, schedules…)"
              className="mt-2 text-xs"
              data-testid={`goal-edit-desc-${goal.id}`}
            />
          ) : (
            goal.description && <p className="text-xs text-muted-foreground mt-1">{goal.description}</p>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {editing ? (
            <>
              <Button size="sm" onClick={save} disabled={saving} data-testid={`goal-save-${goal.id}`}>
                <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => {
                setEditing(false);
                setTitle(goal.title || "");
                setDescription(goal.description || "");
                setItems(Array.isArray(goal.support_items) ? goal.support_items.slice() : []);
              }} data-testid={`goal-cancel-${goal.id}`}>
                <X className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`goal-edit-${goal.id}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={remove} data-testid={`remove-goal-${goal.id}`}>
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-md bg-secondary/40 border border-border p-2">
        <div className="flex items-center gap-1.5 mb-1.5">
          <ListChecks className="w-3.5 h-3.5 text-primary" />
          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
            PCSP Support sub-checkboxes ({items.length})
          </p>
        </div>
        {items.length === 0 && (
          <p className="text-[11px] text-muted-foreground italic">
            No support items yet. {editing ? "Add prompts the contractor will check off below." : "Click Edit to add support prompts."}
          </p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-border text-[11px]" data-testid={`goal-item-${goal.id}-${i}`}>
              <Check className="w-3 h-3 text-emerald-600" />
              <span className="font-mono">{it}</span>
              {editing && (
                <button type="button" onClick={() => removeItem(i)} className="hover:text-red-600 ml-0.5" data-testid={`goal-item-remove-${goal.id}-${i}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {editing && (
          <div className="mt-2 flex items-center gap-2">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
              placeholder="Add a support prompt (e.g. 'Hygiene reminder')"
              className="h-8 text-xs"
              data-testid={`goal-item-input-${goal.id}`}
            />
            <Button size="sm" variant="outline" type="button" onClick={addItem} data-testid={`goal-item-add-${goal.id}`}>
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
